import React from 'react';
import { Box, Grid } from '@mui/material';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';
import Card from '../dish-menu-creator/Card';
import _ from 'lodash';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { FirstScreenMediaCardHeader } from './first-screen-media-card-header.component';
import { FirstScreenBundledContext, type FirstScreenBundledContextValue } from './first-screen-bundled-context';
import { getRBPStringId } from '../main/main-page-states';
import apiManage from '../../request';
import { ACTIVITY_BULLETIN_MAX_SLOTS, withImageCacheBust } from '../../utils/restaurant-static-bundled';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { tFirstScreenCardTitleWithSuggestedImageSize } from './first-screen-card-title-i18n';

/** 活动公告图位数量，与 `ACTIVITY_BULLETIN_MAX_SLOTS`、Figma「已用/总数」一致 */
const ACTIVITY_IMAGE_SLOT_TOTAL = ACTIVITY_BULLETIN_MAX_SLOTS;

/**
 * 第 `slotIndex`（1..N）张活动公告图在 v1 静态资源中的 query/path 文件名（与历史 v3 表单字段一致）。
 * @param slotIndex - 槽位序号，从 1 开始
 */
function activityBulletinFileName(slotIndex: number): string {
    return `activity-bulletin${slotIndex}.jpg`;
}

/**
 * 餐厅活动公告：多图位上传；标题行右侧展示已用槽位与通用/COP、QOP 渠道标识（对齐 Figma 1838:52035）。
 * 展示依赖 bundled；上传/删除走 v1 接口；上传控件与菜单库一致（`RestaurantStaticImageUpload` + loading + Figma 空态）。
 */
export default class ActivityBulletinComponent extends React.Component<Props> {
    /** 由 `FirstScreenBundledProvider` 注入 bundled 与刷新方法 */
    public static contextType = FirstScreenBundledContext;

    /**
     * React 通过 `static contextType` 注入的首页 bundled（不使用 `declare context`，避免 CRA Babel 与 class-fields 插件冲突）。
     */
    private get bundledPageContext(): FirstScreenBundledContextValue | null {
        return (this as unknown as { context: FirstScreenBundledContextValue | null }).context;
    }

    /**
     * 更换图片：上传至 v1 `bulletin-activity-image`，query `name` 指定文件名。
     * @param file - 上传文件
     * @param slotIndex - 图位序号（1..ACTIVITY_IMAGE_SLOT_TOTAL）
     */
    async onChangeAvatarUrl(file: File, slotIndex: number) {
        const stringId = getRBPStringId();
        const name = activityBulletinFileName(slotIndex);
        const result = await apiManage.post_restaurant_static_bulletin_activity_image(stringId, file, name);
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.bundledPageContext?.reloadBundled();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 删除图片：按文件名调用 v1 DELETE。
     * @param slotIndex - 图位序号（1..ACTIVITY_IMAGE_SLOT_TOTAL）
     */
    async deleteImage(slotIndex: number) {
        const stringId = getRBPStringId();
        const urls = this.bundledPageContext?.bundled.activityBulletinUrls ?? [];
        const fromBundled = String(urls[slotIndex - 1] ?? '').trim();
        /** 删除路径上的 name：优先从 bundled URL 取末段文件名，否则用约定名 */
        let name = activityBulletinFileName(slotIndex);
        if (fromBundled) {
            try {
                const seg = new URL(fromBundled).pathname.split('/').filter(Boolean).pop();
                if (seg) {
                    name = decodeURIComponent(seg);
                }
            } catch {
                const parts = fromBundled.split('/');
                const last = parts[parts.length - 1]?.split('?')[0];
                if (last) {
                    name = decodeURIComponent(last);
                }
            }
        }
        const result = await apiManage.delete_restaurant_static_bulletin_activity_image(stringId, name);
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.bundledPageContext?.reloadBundled();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 渲染活动公告卡。
     */
    render() {
        /** 云 RBP 子功能顶栏「辅助模式」 */
        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;
        const ctx = this.bundledPageContext;
        const bust = ctx?.bustVersion ?? 0;
        const urls = ctx?.bundled.activityBulletinUrls ?? [];
        const activityImageSlotUsed = urls
            .slice(0, ACTIVITY_IMAGE_SLOT_TOTAL)
            .filter((u) => String(u || '').trim()).length;
        const stringId = getRBPStringId();

        return (
            <Card
                className="common-card"
                title={(
                    <Box sx={{ width: '100%' }}>
                        <FirstScreenMediaCardHeader
                            title={tFirstScreenCardTitleWithSuggestedImageSize(
                                'first_screen_setting_page_restaurant_activity_notice',
                            )}
                            usedCount={activityImageSlotUsed}
                            totalCount={ACTIVITY_IMAGE_SLOT_TOTAL}
                        />
                        <SubFunctionAuxiliaryHint
                            enabled={auxiliaryModeEnabled}
                            i18nKey="first_screen_aux_activity_bulletin"
                        />
                    </Box>
                )}
                contentRender={(
                    <Grid container spacing={3}>
                        {Array(ACTIVITY_IMAGE_SLOT_TOTAL)
                            .fill(1)
                            .map((_item, index) => {
                                const slotIndex = index + 1;
                                const rawUrl = String(urls[index] ?? '').trim();
                                const displayUrl = withImageCacheBust(rawUrl, bust) ?? '';
                                return (
                                    <Grid item key={index}>
                                        <RestaurantStaticImageUpload
                                            stringId={stringId}
                                            kind="dish-image"
                                            uploadEntityId={1}
                                            deferStaticUpload
                                            imageRef={rawUrl}
                                            displayUrl={displayUrl}
                                            onStagedStaticFile={async (file) => {
                                                await this.onChangeAvatarUrl(file, slotIndex);
                                            }}
                                            onDeferredStaticRemove={async () => {
                                                await this.deleteImage(slotIndex);
                                            }}
                                            uploadEmptyVariant="firstScreenGallery"
                                            slotWidth={256}
                                            slotHeight={160}
                                            sx={{ minHeight: 160 }}
                                        />
                                    </Grid>
                                );
                            })}
                    </Grid>
                )}
            />
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};
